import Button from 'components/form/Button';
import CheckBox from 'components/form/CheckBox';
import TextInput from 'components/form/TextInput';
import { Table } from 'react-bootstrap';
import { cloneDeep, filter, isEqual, map, unionBy } from 'lodash';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchEventPriceCategories,
  handleEditRequestEvent,
  showSuccess,
  throwError,
} from 'store/slices';
import { icons } from 'utils/constants';
import {
  getDataFromLocalStorage,
  titleCaseString,
  trimAllSpace,
  trimLeftSpace,
} from 'utils/helpers';
import Loader from 'components/Layout/Loader';
import CreatablePricingDropdown from 'components/form/CreatablePricingDropdown';
import Label from 'components/form/Label';
import RadioInput from 'components/form/RadioInput';
import '../../../../../EventManagement/Events/EventForm/RegistrationEventForm/RegistrationEventForm.scss';
import usePermission from 'hooks/usePermission';

// Helper functions moved outside the component
const useFilteredData = (pricingCategories, pricingCategoriesList) => {
  return useMemo(() => {
    const uniqueCategories = new Map();

    pricingCategories?.forEach(({ id, category }) => {
      uniqueCategories.set(category, { id, name: category });
    });

    pricingCategoriesList?.forEach(({ id, name, status }) => {
      if (status === '1' && !uniqueCategories.has(name)) {
        uniqueCategories.set(name, { id, name });
      }
    });

    return Array.from(uniqueCategories.values());
  }, [pricingCategories, pricingCategoriesList]);
};

const useFilteredList = (eventPricing, registrationCategoriesList) => {
  return useMemo(() => {
    const categories = new Set(eventPricing?.map((item) => item?.category));
    return registrationCategoriesList?.filter(
      (item) => categories?.has(item?.name) || ['1']?.includes(item?.is_display)
    );
  }, [eventPricing, registrationCategoriesList]);
};

const RegistrationEventRequestForm = ({
  handleNext,
  isViewMode,
  fetchEventDetails
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { eventId } = params || {};
  const userData = getDataFromLocalStorage();
  const { exchange_rate } = getDataFromLocalStorage();
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission("Requests");

  const { allMembershipList, send_to, requestEventData, inrPrice } =
    useSelector((state) => ({
      allMembershipList: state.global.membershipList,
      send_to: state.global.requestEventData?.event_updation?.send_to || '',
      requestEventData: state.global.requestEventData,
      inrPrice: state.currency.inrPrice
    }));
  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [isCateLoading, setIsCateLoading] = useState(false);
  const [pricingCategoriesList, setPricingCategoriesList] = useState([]);
  const [registrationCategoriesList, setRegistrationCategoriesList] = useState([]);
  const [category, setCategory] = useState([]);
  const [categoryBtnLoading, setCategoryBtnLoading] = useState(false);
  const [deleteLoading, setDeleteBtnLoading] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [oldData, setOldData] = useState(null);


  const [data, setData] = useState({
    pricing_categories: [{ id: '', category: '' }],
    event_pricing: [],
    add_on_pricing: [],
    dashboard_pricing_selection: [],
    currency_type: 'USD',
  });

  // Memoized derived data
  const membershipList = useMemo(() =>
    filter(allMembershipList, (e) => send_to?.split(',').includes(e.id)),
    [allMembershipList, send_to]
  );

  const isUpcomingEvent = useMemo(() => requestEventData?.status === 'Upcoming', [requestEventData]);



  // Use the helper hooks
  const showPrcingCategories = useFilteredData(data.pricing_categories, pricingCategoriesList);
  const showEventPricingCategories = useFilteredList(data.event_pricing, registrationCategoriesList);

  const getEventPriceCategories = useCallback(async () => {
    const response = await dispatch(fetchEventPriceCategories());
    setPricingCategoriesList(response?.data?.event_pricing || []);
    setRegistrationCategoriesList(response?.data?.pricing_category || []);
    setIsCateLoading(false);
  }, [dispatch]);

  // Data processing functions
  const getData = useCallback((resData) => {
    const {
      pricing_categories,
      event_pricing: eventPricing,
      add_on_pricing,
      dashboard_pricing_selection,
      currency_type
    } = resData;

    let event_pricing = eventPricing?.map((el) => ({
      ...el,
      physical: el?.physical || [],
      virtual: el?.virtual || [],
    }));

    const categories = map(pricing_categories, (el) => ({
      ...el,
      oral: '',
      poster: '',
      onSpot: '',
    }));

    const memberList = map(membershipList, (elem) => {
      const findData = dashboard_pricing_selection?.find((o) =>
        isEqual(o.id + '', elem.id))?.data || [];

      const newfindData = unionBy(
        findData,
        pricing_categories?.map((el) => ({ ...el, is_checked: false })),
        'id'
      );

      return {
        ...elem,
        data: newfindData.length > 0 ? newfindData :
          map(pricing_categories, (el) => ({ ...el, is_checked: false })),
      };
    });


    const dataWithOralPosterPrice = [{
      id: '',
      category: '',
      physical: cloneDeep(categories),
      virtual: cloneDeep(categories),
    }];

    let setList = event_pricing?.length > 0 ? event_pricing : dataWithOralPosterPrice;

    if (event_pricing?.length > 0) {
      const diff = categories?.length - event_pricing[0]?.physical.length;

      if (diff > 0) {
        const physicalCategories = new Set(
          event_pricing[0]?.physical.map((phyEle) => phyEle.category)
        );

        const arr = categories.filter(
          (ele) => !physicalCategories.has(ele.category)
        );

        setList = setList.map((elm) => ({
          ...elm,
          physical: cloneDeep([...elm.physical, ...arr]),
          virtual: cloneDeep([...elm.virtual, ...arr]),
        }));
      } else if (diff < 0) {
        setList = setList.map((elm) => ({
          ...elm,
          physical: cloneDeep(elm.physical.slice(0, diff)),
          virtual: cloneDeep(elm.virtual.slice(0, diff)),
        }));
      }
    }

    const newData = {
      ...data,
      pricing_categories: pricing_categories?.length > 0 ?
        pricing_categories : [{ id: '', category: '' }],
      event_pricing: setList,
      add_on_pricing: add_on_pricing?.length > 0 ?
        add_on_pricing : [{
          id: '',
          category: '',
          premium_members: '',
          non_premium_members: '',
        }],
      dashboard_pricing_selection: memberList,
      currency_type: currency_type,
    };
    setData(newData);
    setOldData(newData);
    setIsLoading(false);
  }, [data, membershipList]);


  useEffect(() => {
    setCategory(requestEventData?.registration_data?.pricing_categories);
    getData(requestEventData?.registration_data || {});
  }, [requestEventData?.registration_data]);

  const inrValue = inrPrice || exchange_rate;
  const convertPrice = useCallback((price, fromCurrency, toCurrency) => {
    if (!price) return '';

    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) return price;

    if (fromCurrency === 'INR' && toCurrency === 'USD') {
      return (numericPrice / inrValue).toFixed(2);
    } else if (fromCurrency === 'USD' && toCurrency === 'INR') {
      return (numericPrice * inrValue).toFixed(2);
    }
    return price;
  }, [inrValue]);

  const handleCurrencyChange = useCallback((e) => {
    const newCurrency = e.target.value;
    setData((prevData) => {
      const fromCurrency = prevData.currency_type; // ✅ always use current currency

      // Convert event pricing
      const updatedEventPricing = prevData.event_pricing.map((event) => ({
        ...event,
        physical: event.physical.map((item) => ({
          ...item,
          oral: convertPrice(item.oral, fromCurrency, newCurrency),
          poster: convertPrice(item.poster, fromCurrency, newCurrency),
          onSpot: convertPrice(item.onSpot, fromCurrency, newCurrency),
        })),
        virtual: event.virtual.map((item) => ({
          ...item,
          oral: convertPrice(item.oral, fromCurrency, newCurrency),
          poster: convertPrice(item.poster, fromCurrency, newCurrency),
          onSpot: convertPrice(item.onSpot, fromCurrency, newCurrency),
        })),
      }));

      // Convert add-on pricing
      const updatedAddOnPricing = prevData.add_on_pricing.map((item) => ({
        ...item,
        premium_members: convertPrice(item.premium_members, fromCurrency, newCurrency),
        non_premium_members: convertPrice(item.non_premium_members, fromCurrency, newCurrency),
      }));

      return {
        ...prevData,
        currency_type: newCurrency, // ✅ update currency here
        event_pricing: updatedEventPricing,
        add_on_pricing: updatedAddOnPricing,
      };
    });

    // update initialCurrency separately if you still need it elsewhere
  }, [convertPrice]);


  // Form actions
  const updateCategory = async () => {
    setCategoryBtnLoading(true);
    let payload = data?.pricing_categories?.map((elm) => {
      return { ...elm, request_id: eventId };
    });

    const resposne = await dispatch(
      handleEditRequestEvent({
        request_id: eventId,
        registration_data: true,
        ...data,
        pricing_categories: payload,
      })
    );
    if (resposne.status === 200) {
      localStorage.removeItem('categories');
      // getPricing();
      // getEventPriceCategories();
      fetchEventDetails();
    }
    setCategoryBtnLoading(false);
  };


  const addCategory = useCallback((type) => {
    setData((oldData) => {
      const newData = cloneDeep(oldData);

      if (type === 'pricing_categories') {
        newData.pricing_categories = [
          ...newData.pricing_categories,
          { id: '', category: '' },
        ];
      } else if (type === 'event_pricing') {
        const categories = map(category, (el) => ({
          ...el,
          oral: '',
          poster: '',
          onSpot: '',
        }));

        newData.event_pricing = [
          ...newData.event_pricing,
          {
            id: '',
            physical: cloneDeep(categories),
            virtual: cloneDeep(categories),
          },
        ];
      } else if (type === 'add_on_pricing') {
        newData.add_on_pricing = [
          ...newData.add_on_pricing,
          { id: '', category: '', premium_members: '', non_premium_members: '' },
        ];
      }

      return newData;
    });
  }, [category]);

  const deleteCategory = async (type, index, id) => {
    let oldData = cloneDeep(data);
    setDeleteBtnLoading(`${type}_${index}`);
    if (type === 'pricing_categories') {
      oldData.pricing_categories.splice(index, 1);
    } else if (type === 'event_pricing') {
      oldData.event_pricing.splice(index, 1);
    } else if (type === 'add_on_pricing') {
      oldData.add_on_pricing.splice(index, 1);
    } else {
      // Nothing
    }
    setDeleteBtnLoading('');
    setData(oldData);
  };



  const handelChangeInput = useCallback((e) => {
    const id = e.target.id;
    const sID = id.split('-');
    let value = trimLeftSpace(titleCaseString(e.target.value));

    setData((oldData) => {
      const newData = cloneDeep(oldData);

      if (sID[0] === 'pricing_categories') {
        newData.pricing_categories[sID[1]].category = value;
      } else if (sID[0] === 'event_pricing') {
        if (sID.length > 2) {
          // Example ID: event_pricing-0-physical-123-oral
          const pricingIndex = parseInt(sID[1]);
          const mode = sID[2]; // physical / virtual
          const categoryId = sID[3];
          const field = sID[4]; // oral / poster / onSpot

          const categoryIndex = newData.event_pricing[pricingIndex][mode].findIndex(
            (item) => item.id === categoryId || item.id.toString() === categoryId
          );

          if (categoryIndex !== -1) {
            let sanitized = trimAllSpace(value).replace(/[^0-9.]/g, '');
            sanitized = sanitized.replace(/(\..*?)\..*/g, '$1');
            newData.event_pricing[pricingIndex][mode][categoryIndex][field] = sanitized;
          }
        } else {
          newData.event_pricing[sID[1]].category = value;
        }

      } else if (sID[0] === 'add_on_pricing') {
        if (sID[2] !== 'category') {
          value = trimAllSpace(value.replace(/\D/g, ''));
        }
        newData.add_on_pricing[sID[1]][sID[2]] = value;
      }

      return newData;
    });
  }, []);

  const updatePrice = useCallback(async (data) => {
    setIsBtnLoading(true);

    const isAddOnBtn = data.add_on_pricing.every(
      (o) => o.category && o.premium_members && o.non_premium_members
    );

    if (!isAddOnBtn) {
      data = { ...data, add_on_pricing: [] };
    }

    const payload = { ...data, request_id: eventId, registration_data: true };

    const response = await dispatch(handleEditRequestEvent(payload));
    if (response?.status === 200) {
      localStorage.removeItem('categories');
      // getEventPriceCategories();
      const text = oldData?.event_pricing?.[0]?.category ? 'update' : 'add';
      dispatch(showSuccess(response?.message || `Registration price ${text} successfully.`));
      fetchEventDetails();
    }
    setIsBtnLoading(false);
  }, [dispatch, eventId, oldData, handleNext]);

  // Memoized UI helpers
  const filtedListForPresentEvent = useMemo(() =>
    showPrcingCategories.filter((item) => item.id !== '' && item.name !== ''),
    [showPrcingCategories]
  );

  const filteredCategories = useMemo(() =>
    registrationCategoriesList?.filter((category) => {
      const isInEventPricing = data.event_pricing.some(
        (pricing) => pricing?.category === category?.name
      );
      const isActive = category?.status === '1';
      const isInactiveAndMatchingUser =
        category?.status === '0' && category?.user_id === userData?.id;

      return isInEventPricing || isActive || isInactiveAndMatchingUser;
    }),
    [registrationCategoriesList, data.event_pricing, userData]
  );

  // Validation flags
  const isPriceBtn = useMemo(() =>
    data.pricing_categories.every((o) => o.category),
    [data.pricing_categories]
  );

  const isAddOnBtn = useMemo(
    () =>
      Array.isArray(data?.add_on_pricing) &&
      data.add_on_pricing.every(
        o => o?.category && o?.premium_members && o?.non_premium_members
      ),
    [data?.add_on_pricing]
  );


  const isEventBtn = useMemo(() => {
    return data.event_pricing?.every((elm) => {
      if (!elm.category) return false;

      const checkPhysical =
        requestEventData?.event_updation?.event_mode?.includes("Physical") &&
        elm.physical.some((o) => !o.oral || !o.poster || !o.onSpot);

      const checkVirtual =
        requestEventData?.event_updation?.event_mode?.includes("Virtual") &&
        elm.virtual.some((o) => !o.oral || !o.poster || !o.onSpot);

      return !checkPhysical && !checkVirtual;
    });
  }, [data.event_pricing, requestEventData?.event_updation?.event_mode]);

  const isAnyCheck = useMemo(() => {
    for (const o of data.dashboard_pricing_selection) {
      for (const c of o.data) {
        if (c.is_checked) {
          return true;
        }
      }
    }
    return false;
  }, [data?.dashboard_pricing_selection]);

  const isCategory = useMemo(() =>
    data.pricing_categories && data.pricing_categories[0]?.id ? true : false,
    [data.pricing_categories]
  );

  const isbtnEnable = useMemo(() =>
    isEqual(oldData, data) || !isEventBtn || !isAnyCheck,
    [oldData, data, isEventBtn, isAnyCheck]
  );

  const isCategoryBtn = useMemo(() =>
    data.pricing_categories?.every((o) => !o.category),
    [data.pricing_categories]
  );




  useEffect(() => {
    getEventPriceCategories();
  }, [getEventPriceCategories]);

  if (isLoading) {
    return (

      <div className="cpt-80 cpb-80">
        <Loader size="md" />
      </div>
    );
  }

  return (
    <div id="new-registration-event-form-block">
      <div className="d-flex gap-2 align-items-center mb-3 ">

        <div className="text-18-500 color-black-olive">
          Pricing Categories
        </div>
      </div>

      {/* Pricing Categories Section */}
      <div className="row">
        {data.pricing_categories?.map((elem, index) => {
          const isDisableForUpcoming = showPrcingCategories?.find(
            (o) => o?.name === elem.category && o?.status === "0" && o?.is_display === "1"
          )?.status === "0";

          return (
            <div className="col-lg-6 col-md-6 col-12 cmb-22" key={index}>
              <div className="row">
                <div className="col-xl-9 col-md-9 col-9">
                  <CreatablePricingDropdown
                    label={`Pricing Category ${index + 1}`}
                    id={`pricing_categories-${index}`}
                    placeholder="Select Price Category"
                    options={elem?.category ? showPrcingCategories : filtedListForPresentEvent}
                    optionKey={"name"}
                    optionValue={"name"}
                    value={elem?.category}
                    onChange={handelChangeInput}
                    isLoading={isCateLoading}
                    selectedList={data.pricing_categories || []}
                    disabled={
                      isViewMode ||
                      (userData?.user_type === '6'
                        ? isUpcomingEvent
                          ? isDisableForUpcoming
                          : ''
                        : '')
                    }
                  />
                </div>
                <div className="col-xl-3 col-md-3 col-3 d-flex align-items-center cpt-18">
                  {data.pricing_categories?.length > 1 && deleteAccess === "1" && (
                    <Button
                      icon={<i className="bi bi-trash fs-5" />}
                      btnStyle="primary-outline"
                      className="h-38"
                      onClick={() => deleteCategory("pricing_categories", index)}
                      disabled={isViewMode}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="d-flex gap-3">
        {(updateAccess === '1' || createAccess === '1') && (
          <>
            <Button
              text={isCategory ? 'Update' : 'Add'}
              btnStyle="primary-dark"
              className="text-15-500 cps-20 cpe-20 h-37"
              disabled={isCategory ? isEqual(data.pricing_categories, category) : isCategoryBtn || isViewMode}
              onClick={updateCategory}
              btnLoading={categoryBtnLoading}
            />
            <Button
              text="+ Add Another Category"
              btnStyle="primary-light-outline"
              className="text-15-500 cps-20 cpe-20 h-37"
              onClick={() => addCategory('pricing_categories')}
              disabled={!isPriceBtn || isViewMode}
            />
          </>
        )}
      </div>

      <div className="d-flex align-items-center cmt-24 gap-4">
        <Label label="Currency Type:" />
        <div className="d-flex gap-3">
          {["INR", "USD"].map((currency) => (
            <RadioInput
              key={currency}
              label={currency}
              value={currency}
              onChange={handleCurrencyChange}
              checked={data?.currency_type === currency}
              disabled={isViewMode}
            />
          ))}
        </div>
      </div>


      <div className="d-flex align-items-center cmt-24 gap-4">
        <Label label="Event Type :" />
        <div className="d-flex gap-4">
          <label className="d-flex align-items-center gap-2">
            <CheckBox
              type="ACTIVE"
              isChecked={requestEventData?.event_updation?.event_mode?.includes("Physical")}
              disabled={true}
            />
            <span>Physical</span>
          </label>
          <label className="d-flex align-items-center gap-2">
            <CheckBox
              type="ACTIVE"
              isChecked={requestEventData?.event_updation?.event_mode?.includes("Virtual")}
              disabled={true}
            />
            <span>Virtual</span>
          </label>
        </div>
      </div>


      {/* Event Pricing Section */}
      {isCategory && (
        <>
          <div className="text-18-500 color-black-olive mb-3 cpt-30">
            Event Pricing
          </div>

          <div className="table-responsive">
            <Table bordered responsive className="mb-0">
              <thead className="table-header-container">
                <tr className="table-header-row-container">
                  {requestEventData?.event_updation?.event_mode?.split(",")?.map((mode, idx) => (
                    <th className="text-center header-cell " key={idx} colSpan={3}>
                      {mode.trim()}
                    </th>
                  ))}
                </tr>
                <tr>
                  {requestEventData?.event_updation?.event_mode?.split(",")?.map((mode, idx) => (
                    <React.Fragment key={idx}>
                      <th className="text-center bg-eff3 text-14-600 color-new-car py-3">Early Bird</th>
                      <th className="text-center bg-eff3 text-14-600 color-new-car py-3">Final Price</th>
                      <th className="text-center bg-eff3 text-14-600 color-new-car py-3">On Spot Price</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>

              <tbody>
                {data.event_pricing?.map((elem, cIdx) => {
                  const { physical = [], virtual = [], category } = elem;
                  const isDisableForUpcomingEventPricing = showEventPricingCategories?.find(
                    (o) => o?.name === elem.category && o?.status === '0' && o?.is_display === '1'
                  )?.status === '0';

                  return (
                    <React.Fragment key={cIdx}>
                      <tr>
                        <td colSpan={requestEventData?.event_updation?.event_mode?.split(",")?.length * 3}>
                          <div className="cps-32 d-flex justify-content-start gap-3 align-items-center">
                            <div className='d-flex align-items-center gap-3'>
                              Registration Categories :
                              <CreatablePricingDropdown
                                id={`event_pricing-${cIdx}`}
                                placeholder="Select Category"
                                options={filteredCategories}
                                optionKey={'name'}
                                optionValue={'name'}
                                value={elem?.category}
                                onChange={handelChangeInput}
                                isLoading={isCateLoading}
                                selectedList={data.event_pricing || []}
                                disabled={
                                  userData?.user_type === '6' && isUpcomingEvent && isDisableForUpcomingEventPricing
                                  || isViewMode}
                              />
                            </div>

                            {data.event_pricing.length > 1 && (
                              <td className="text-center">
                                <Button
                                  btnStyle="light-outline"
                                  className="pt-1 pb-1 ps-2 pe-2 h-35"
                                  icon={
                                    deleteLoading === `event_pricing_${cIdx}` ? (
                                      ""
                                    ) : (
                                      <img
                                        src={icons.deleteIcon}
                                        alt="delete"
                                        className="h-21"
                                      />
                                    )
                                  }
                                  onClick={() => deleteCategory("event_pricing", cIdx, elem.id)}
                                  btnLoading={deleteLoading === `event_pricing_${cIdx}`}
                                  disabled={isViewMode}
                                />
                              </td>
                            )}
                          </div>
                        </td>
                      </tr>

                      {(physical || []).map((cat, index) => {
                        const virtualCat = virtual.find(v => v.id === cat.id) || {};

                        return (
                          <React.Fragment key={cat.id || index}>
                            <tr>
                              <td
                                colSpan={requestEventData?.event_updation?.event_mode?.split(",")?.length * 3}
                                className="text-14-500 text-center bg-f5f5ff py-3"
                              >
                                {cat?.category}
                              </td>
                            </tr>

                            <tr>
                              {requestEventData?.event_updation?.event_mode?.split(",")?.map((mode, idx) => {
                                const modeKey = mode.trim().toLowerCase();

                                if (modeKey === 'physical') {
                                  return (
                                    <React.Fragment key={`physical-${cat.id}-${idx}`}>
                                      <td>
                                        <TextInput
                                          id={`event_pricing-${cIdx}-physical-${cat.id}-oral`}
                                          placeholder="Early Bird"
                                          value={cat.oral || ""}
                                          onChange={handelChangeInput}
                                          type='number'
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td>
                                        <TextInput
                                          id={`event_pricing-${cIdx}-physical-${cat.id}-poster`}
                                          placeholder="Final Price"
                                          value={cat.poster || ""}
                                          onChange={handelChangeInput}
                                          type='number'
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td>
                                        <TextInput
                                          id={`event_pricing-${cIdx}-physical-${cat.id}-onSpot`}
                                          placeholder="On Spot Price"
                                          value={cat.onSpot || ""}
                                          onChange={handelChangeInput}
                                          type='number'
                                          disabled={isViewMode}
                                        />
                                      </td>
                                    </React.Fragment>
                                  );
                                }

                                if (modeKey === 'virtual') {
                                  return (
                                    <React.Fragment key={`virtual-${cat.id}-${idx}`}>
                                      <td>
                                        <TextInput
                                          id={`event_pricing-${cIdx}-virtual-${cat.id}-oral`}
                                          placeholder="Early Bird"
                                          value={virtualCat.oral || ""}
                                          onChange={handelChangeInput}
                                          type='number'
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td>
                                        <TextInput
                                          id={`event_pricing-${cIdx}-virtual-${cat.id}-poster`}
                                          placeholder="Final Price"
                                          value={virtualCat.poster || ""}
                                          onChange={handelChangeInput}
                                          type='number'
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td>
                                        <TextInput
                                          id={`event_pricing-${cIdx}-virtual-${cat.id}-onSpot`}
                                          placeholder="On Spot Price"
                                          value={virtualCat.onSpot || ""}
                                          onChange={handelChangeInput}
                                          type='number'
                                          disabled={isViewMode}
                                        />
                                      </td>
                                    </React.Fragment>
                                  );
                                }

                                return null;
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                <tr>
                  <td colSpan={requestEventData?.event_updation?.event_mode?.split(",")?.length * 3}>
                    <div className="d-flex cps-32">
                      <Button
                        text="+ Add Another Category"
                        btnStyle="primary-light-outline"
                        className="text-15-500 ps-4 pe-4 h-37"
                        onClick={() => addCategory("event_pricing")}
                        disabled={isViewMode}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Add On Pricing Section */}
          <div className="text-18-500 color-black-olive mb-3 cpt-30">
            Add On Pricing
          </div>
          <Table bordered>
            <thead className="table-header-container">
              <tr className="table-header-row-container">
                <th className="header-cell">Add On Categories</th>
                <th className="header-cell">Premium Members</th>
                <th className="header-cell">Non-Premium Members</th>
                {data.add_on_pricing.length > 1 && <th className="header-cell" />}
              </tr>
            </thead>
            <tbody className="table-body-container">
              {Array.isArray(data?.add_on_pricing) &&
                data?.add_on_pricing.map((elem, index) => (
                  <tr className="table-body-row-container" key={index}>
                    <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
                      <TextInput
                        id={`add_on_pricing-${index}-category`}
                        onChange={handelChangeInput}
                        placeholder="Enter Add ons Category"
                        value={elem.category}
                        disabled={isViewMode}
                      />
                    </td>
                    <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
                      <TextInput
                        id={`add_on_pricing-${index}-premium_members`}
                        onChange={handelChangeInput}
                        placeholder="Enter Price"
                        value={elem.premium_members}
                        type='number'
                        disabled={isViewMode}
                      />
                    </td>
                    <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
                      <TextInput
                        id={`add_on_pricing-${index}-non_premium_members`}
                        onChange={handelChangeInput}
                        placeholder="Enter Price"
                        value={elem.non_premium_members}
                        type='number'
                        disabled={isViewMode}
                      />
                    </td>

                    {data.add_on_pricing.length > 1 && (
                      <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
                        <Button
                          isSquare
                          btnStyle="light-outline"
                          className="pt-1 pb-1 ps-2 pe-2 h-35"
                          icon={
                            deleteLoading === `add_on_pricing_${index}` ? (
                              ''
                            ) : (
                              <img
                                src={icons.deleteIcon}
                                alt="delete"
                                className="h-21"
                              />
                            )
                          }
                          onClick={() => deleteCategory('add_on_pricing', index, elem.id)}
                          btnLoading={deleteLoading === `add_on_pricing_${index}`}
                          disabled={isViewMode}
                        />
                      </td>
                    )}
                  </tr>
                ))}

              <tr>
                <td colSpan={4}>
                  <div className="d-flex cps-32">
                    <Button
                      text="+ Add Another Category"
                      btnStyle="primary-light-outline"
                      className="text-15-500 ps-4 pe-4 h-37"
                      disabled={!isAddOnBtn || isViewMode}
                      onClick={() => addCategory('add_on_pricing')}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </Table>

          {/* Dashboard Pricing Selection Section */}
          <div className="text-18-500 color-black-olive mb-3 cpt-30">
            Dashboard Pricing Selection
          </div>
          <Table bordered>
            <thead className="table-header-container">
              <tr className="table-header-row-container">
                <th className="header-cell">Registration Categories</th>
                {Object.entries(
                  data?.dashboard_pricing_selection?.reduce((acc, item) => {

                    const [membership, type] = item.name.split("-");
                    if (!acc[membership]) acc[membership] = [];
                    acc[membership].push(type);
                    return acc;
                  }, {})
                ).map(([membership, types], i) => (
                  <th
                    key={i}
                    className="header-cell text-center"
                    colSpan={types.length}
                  >
                    {membership}
                  </th>
                ))}
              </tr>

              <tr>
                <th className='bg-eff3 py-3'></th>
                {data?.dashboard_pricing_selection?.map((item, i) => {
                  const [, type] = item.name.split("-");
                  return (
                    <th key={i} className="header-cell text-center bg-eff3 text-14-600 color-new-car py-3">
                      {type}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="table-body-container">
              <tr className="table-body-row-container">
                <td className="text-14-600 color-black-olive ps-2 pe-2 text-center">
                  Select All
                </td>
                {data.dashboard_pricing_selection.map((p, pIndex) => {
                  const isAllChecked = p.data.every((c) => c.is_checked);
                  return (
                    <td key={pIndex} className="text-14-400 color-black-olive row-cell">
                      <div className="center-flex">
                        <CheckBox
                          type="ACTIVE"
                          isChecked={isAllChecked}
                          onClick={() => {
                            setData((oldData) => {
                              const newData = cloneDeep(oldData);
                              newData.dashboard_pricing_selection[pIndex].data =
                                newData.dashboard_pricing_selection[pIndex].data.map((c) => ({
                                  ...c,
                                  is_checked: !isAllChecked,
                                }));
                              return newData;
                            });
                          }}
                          disabled={isViewMode}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>

              {category?.map((cat, catIndex) => (
                <tr key={catIndex} className="table-body-row-container">
                  <td className="text-14-600 color-black-olive ps-2 pe-2 text-center">
                    {cat.category}
                  </td>
                  {data.dashboard_pricing_selection.map((p, pIndex) => {
                    const cElem = p.data.find((c) => c.id === cat.id);
                    return (
                      <td key={pIndex} className="text-14-400 color-black-olive row-cell">
                        <div className="center-flex">
                          <CheckBox
                            type="ACTIVE"
                            isChecked={cElem?.is_checked}
                            onClick={() => {
                              setData((oldData) => {
                                const newData = cloneDeep(oldData);
                                const item = newData.dashboard_pricing_selection[pIndex].data.find(
                                  (c) => c.id === cat.id
                                );
                                item.is_checked = !item.is_checked;
                                return newData;
                              });
                            }}
                            disabled={isViewMode}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>

          <>
            {!isViewMode && (
              <div className="d-flex justify-content-center gap-4 cmt-40 cmb-20">
                {(updateAccess === '1' || createAccess === '1') && (
                  <Button
                    text="Save"
                    btnStyle="primary-dark"
                    className="cps-40 cpe-40 h-37"
                    btnLoading={isBtnLoading}
                    disabled={isbtnEnable}
                    onClick={() => {
                      if (!isEventBtn) {
                        dispatch(
                          throwError({
                            message:
                              'Please enter a amount in the event pricing table.',
                          })
                        );
                        return;
                      }
                      updatePrice(data);
                    }}
                  />
                )}
                <Button
                  text="Reset"
                  btnStyle="light-outline"
                  className="cps-40 cpe-40 h-37"
                  onClick={() => {
                    setData(oldData);
                  }}
                />
                <Button
                  text={'Procced Next'}
                  btnStyle="light-outline"
                  className={'cps-20 cpe-20 h-37'}
                  onClick={() => {
                    handleNext('registration');
                  }}
                />
              </div>
            )}
          </>
        </>
      )}
    </div>

  );
};

export default RegistrationEventRequestForm;


//Old Flow
// import Button from 'components/form/Button';
// import CheckBox from 'components/form/CheckBox';
// import TextInput from 'components/form/TextInput';
// import { Table } from 'react-bootstrap';
// import { cloneDeep, filter, forEach, isEqual, map, unionBy } from 'lodash';
// import React, { useEffect, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate, useParams } from 'react-router-dom';
// import {
//   deleteAddOnPricing,
//   deleteEventPricing,
//   fetchEventPriceCategories,
//   handleEditRequestEvent,
//   showSuccess,
//   throwError,
//   updateEventPricing,
//   updatePricingCategory,
// } from 'store/slices';
// import { icons } from 'utils/constants';
// import {
//   getDataFromLocalStorage,
//   titleCaseString,
//   trimAllSpace,
//   trimLeftSpace,
// } from 'utils/helpers';
// import Loader from 'components/Layout/Loader';
// import '../../../../../EventManagement/Events/EventForm/NewRegistrationEventForm.scss';
// import CreatablePricingDropdown from 'components/form/CreatablePricingDropdown';
// import Dropdown from 'components/form/Dropdown';

// const currencyType = [
//   { id: 'USD', label: 'USD' },
//   { id: 'INR', label: 'INR' },
// ];

// const NewRegistrationEventRequestForm = ({
//   eventTab = false,
//   handleNext,
//   isViewMode,
//   fetchEventDetails,
// }) => {
//   const dispatch = useDispatch();
//   const params = useParams();
//   const { eventId } = params || {};
//   const userData = getDataFromLocalStorage();
//   const { allMembershipList, send_to, requestEventData, userDetails } =
//     useSelector((state) => ({
//       allMembershipList: state.global.membershipList,
//       send_to: state.global.requestEventData?.event_updation?.send_to || '',
//       requestEventData: state.global.requestEventData,
//       userDetails: state.student.userDetails,
//     }));

//   const membershipList = filter(allMembershipList, (e) =>
//     send_to?.split(',').includes(e.id)
//   );

//   const [isLoading, setIsLoading] = useState(true);
//   const [isCateLoading, setIsCateLoading] = useState(false);
//   const [pricingCategoriesList, setPricingCategoriesList] = useState([]);
//   const [registrationCategoriesList, setRegistrationCategoriesList] = useState(
//     []
//   );
//   const [category, setCategory] = useState([]);
//   const [categoryBtnLoading, setCategoryBtnLoading] = useState(false);
//   const [deleteLoading, setDeleteBtnLoading] = useState(false);
//   const [isBtnLoading, setIsBtnLoading] = useState(false);
//   const [oldData, setOldData] = useState(null);

//   const [data, setData] = useState({
//     pricing_categories: [
//       {
//         id: '',
//         category: '',
//       },
//     ],
//     event_pricing: [],
//     add_on_pricing: [],
//     dashboard_pricing_selection: [],
//     currency_type: 'USD',
//   });
//   const [initialCurrency, setInitialCurrency] = useState(data?.currency_type);

//   const {
//     // view: viewAccess,
//     update: updateAccess,
//     delete: deleteAccess,
//     create: createAccess,
//   } = userDetails?.role_data?.find(
//     (item) => item?.permission_name === 'Requests'
//   )?.sub_permission || {};

//   const updateCategory = async () => {
//     setCategoryBtnLoading(true);
//     let payload = data?.pricing_categories?.map((elm) => {
//       return { ...elm, request_id: eventId };
//     });

//     const resposne = await dispatch(
//       handleEditRequestEvent({
//         request_id: eventId,
//         registration_data: true,
//         ...data,
//         pricing_categories: payload,
//       })
//     );
//     if (resposne.status === 200) {
//       localStorage.removeItem('categories');
//       // getPricing();
//       // getEventPriceCategories();
//       fetchEventDetails();
//     }
//     setCategoryBtnLoading(false);
//   };

//   const addCategory = (type) => {
//     let oldData = cloneDeep(data);
//     if (type === 'pricing_categories') {
//       oldData.pricing_categories = [
//         ...oldData.pricing_categories,
//         { id: '', category: '' },
//       ];
//     } else if (type === 'event_pricing') {
//       const categories = map(category, (el) => {
//         return { ...el, oral: '', poster: '', onSpot: '' };
//       });

//       oldData.event_pricing = [
//         ...oldData.event_pricing,
//         {
//           id: '',
//           physical: cloneDeep(categories),
//           virtual: cloneDeep(categories),
//         },
//       ];
//     } else if (type === 'add_on_pricing') {
//       oldData.add_on_pricing = [
//         ...oldData.add_on_pricing,
//         { id: '', category: '', premium_members: '', non_premium_members: '' },
//       ];
//     } else {
//       // Nothing
//     }
//     setData(oldData);
//   };

//   const deleteCategory = async (type, index, id) => {
//     let oldData = cloneDeep(data);
//     setDeleteBtnLoading(`${type}_${index}`);
//     if (type === 'pricing_categories') {
//       oldData.pricing_categories.splice(index, 1);
//     } else if (type === 'event_pricing') {
//       oldData.event_pricing.splice(index, 1);
//     } else if (type === 'add_on_pricing') {
//       oldData.add_on_pricing.splice(index, 1);
//     } else {
//       // Nothing
//     }
//     setDeleteBtnLoading('');
//     setData(oldData);
//   };

//   const handelChangeInput = (e) => {
//     const id = e.target.id;
//     const sID = cloneDeep(id.split('-'));
//     let value = trimLeftSpace(titleCaseString(e.target.value));

//     let oldData = cloneDeep(data);

//     if (sID[0] === 'pricing_categories') {
//       oldData.pricing_categories[sID[1]].category = value;
//     } else if (sID[0] === 'event_pricing') {
//       // console.log("sid",sID.length > 2)
//       if (sID.length > 2) {
//         oldData.event_pricing[sID[1]][sID[2]][sID[3]][sID[4]] = trimAllSpace(
//           value
//         ).replace(/\D/g, '');
//         // console.log("else if",oldData.event_pricing)
//       } else {
//         oldData.event_pricing[sID[1]].category = value;
//         // console.log("else ",oldData.event_pricing[sID[1]])
//       }
//     } else if (sID[0] === 'add_on_pricing') {
//       if (sID[2] !== 'category') {
//         value = trimAllSpace(value.replace(/\D/g, ''));
//       }
//       oldData.add_on_pricing[sID[1]][sID[2]] = value;
//     } else {
//       // Nothing
//     }

//     setData(oldData);
//   };

//   const updatePrice = async (data) => {
//     setIsBtnLoading(true);
//     const isAddOnBtn = data.add_on_pricing.every(
//       (o) => o.category && o.premium_members && o.non_premium_members
//     );
//     if (!isAddOnBtn) {
//       data = { ...data, add_on_pricing: [] };
//     }
//     const payload = { ...data, request_id: eventId, registration_data: true };
//     const response = await dispatch(handleEditRequestEvent(payload));
//     if (response?.status === 200) {
//       localStorage.removeItem('categories');
//       // getEventPriceCategories();
//       const text = oldData?.event_pricing?.[0]?.category ? 'update' : 'add';
//       dispatch(showSuccess(`Registration price ${text} successfully.`));
//       fetchEventDetails();
//     }
//     setIsBtnLoading(false);
//   };

//   const getData = (resData) => {
//     const {
//       pricing_categories,
//       event_pricing: eventPricing,
//       add_on_pricing,
//       dashboard_pricing_selection,
//       currency_type,
//     } = resData || {};

//     let event_pricing = eventPricing?.map((el) => {
//       return {
//         ...el,
//         physical: el?.physical || [],
//         virtual: el?.virtual || [],
//       };
//     });
//     const categories = map(pricing_categories, (el) => {
//       return { ...el, oral: '', poster: '', onSpot: '' };
//     });
//     const memberList = map(membershipList, (elem) => {
//       const findData =
//         dashboard_pricing_selection?.find((o) => isEqual(o.id + '', elem.id))
//           ?.data || [];
//       const newfindData = unionBy(
//         findData,
//         pricing_categories?.map((el) => ({ ...el, is_checked: false })),
//         'id'
//       );
//       return {
//         ...elem,
//         data:
//           newfindData.length > 0
//             ? newfindData
//             : map(pricing_categories, (el) => {
//                 return { ...el, is_checked: false };
//               }),
//       };
//     });

//     const dataWithOralPosterPrice = [
//       {
//         id: '',
//         category: '',
//         physical: cloneDeep(categories),
//         virtual: cloneDeep(categories),
//       },
//     ];

//     let setList =
//       event_pricing?.length > 0 ? event_pricing : dataWithOralPosterPrice;

//     if (event_pricing?.length > 0) {
//       if (categories.length !== event_pricing[0]?.physical.length) {
//         let diff = categories.length - event_pricing[0]?.physical.length;

//         if (diff > 0) {
//           const physicalCategories = new Set(
//             event_pricing[0]?.physical.map((phyEle) => phyEle.category)
//           );

//           // Filter categories to find items not in physicalCategories
//           const arr = categories.filter(
//             (ele) => !physicalCategories.has(ele.category)
//           );

//           setList = setList.map((elm) => {
//             return {
//               ...elm,
//               physical: cloneDeep([...elm.physical, ...arr]),
//               virtual: cloneDeep([...elm.virtual, ...arr]),
//             };
//           });
//         } else if (diff < 0) {
//           setList = setList.map((elm) => {
//             return {
//               ...elm,
//               physical: cloneDeep(elm.physical?.slice(-diff) || []),
//               virtual: cloneDeep(elm.virtual?.slice(-diff) || []),
//             };
//           });
//           // setList = setList.map((elm) => {
//           //   return {
//           //     ...elm,
//           //     physical: cloneDeep(
//           //       elm.physical?.splice(-diff, elm.physical.length - 1)
//           //     ),
//           //     virtual: cloneDeep(
//           //       elm.virtual?.splice(-diff, elm.virtual.length - 1)
//           //     ),
//           //   };
//           // });
//         } else {
//           // Nothing
//         }
//       }
//     }
//     const newData = {
//       ...data,
//       pricing_categories:
//         pricing_categories?.length > 0
//           ? pricing_categories
//           : [{ id: '', category: '' }],
//       event_pricing: setList,
//       add_on_pricing:
//         add_on_pricing?.length > 0
//           ? add_on_pricing
//           : [
//               {
//                 id: '',
//                 category: '',
//                 premium_members: '',
//                 non_premium_members: '',
//               },
//             ],
//       dashboard_pricing_selection: memberList,
//       currency_type: currency_type,
//     };

//     setInitialCurrency(newData.currency_type);
//     setData(newData);
//     setOldData(newData);
//     setIsLoading(false);
//   };

//   useEffect(() => {
//     setCategory(requestEventData?.registration_data?.pricing_categories);
//     getData(requestEventData?.registration_data || {});
//   }, [requestEventData?.registration_data]);

//   // dropdown list
//   const getEventPriceCategories = async () => {
//     const response = await dispatch(fetchEventPriceCategories());
//     setPricingCategoriesList(response?.data?.event_pricing || []);
//     setRegistrationCategoriesList(response?.data?.pricing_category || []);
//     setIsCateLoading(false);
//   };

//   useEffect(() => {
//     getEventPriceCategories();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const {
//     pricing_categories: pricingCategories,
//     event_pricing: eventPricing,
//     add_on_pricing: addOnPricing,
//     dashboard_pricing_selection: dashboardPricingSelection,
//   } = data;
//   const isPriceBtn = pricingCategories.every((o) => o.category);
//   const isAddOnBtn = addOnPricing.every(
//     (o) => o.category && o.premium_members && o.non_premium_members
//   );
//   let isEventBtn = true;
//   forEach(eventPricing, (elm) => {
//     if (
//       !elm.category ||
//       elm.physical.some((o) => !o.oral || !o.poster || !o.onSpot) ||
//       elm.virtual.some((o) => !o.oral || !o.poster || !o.onSpot)
//     ) {
//       isEventBtn = false;
//     }
//   });
//   let isAnyCheck = false;
//   forEach(dashboardPricingSelection, (o) => {
//     forEach(o.data, (c) => {
//       if (c.is_checked) {
//         isAnyCheck = true;
//       }
//     });
//   });

//   const isCategory =
//     pricingCategories && pricingCategories[0]?.id ? true : false;
//   const isbtnEnable = isEqual(oldData, data) || !isEventBtn || !isAnyCheck;
//   const isCategoryBtn = pricingCategories?.every((o) => !o.category);

//   //ADD New
//   const useFilteredData = (pricingCategories, pricingCategoriesList) => {
//     const uniqueCategories = new Map();
//     pricingCategories?.forEach(({ id, category }) => {
//       uniqueCategories.set(category, { id, name: category });
//     });

//     pricingCategoriesList?.forEach(({ id, name, status }) => {
//       if (status === '1' && !uniqueCategories.has(name)) {
//         uniqueCategories.set(name, { id, name });
//       }
//     });

//     return Array.from(uniqueCategories.values());
//   };

//   const useFilteredList = (eventPricing, registrationCategoriesList) => {
//     const categories = new Set(eventPricing?.map((item) => item?.category));
//     return registrationCategoriesList?.filter(
//       (item) => categories?.has(item?.name) || ['1']?.includes(item?.is_display)
//     );
//   };

//   const showPrcingCategories =
//     useFilteredData(pricingCategories, pricingCategoriesList) || [];

//   const showEventPricingCategories =
//     useFilteredList(eventPricing, registrationCategoriesList) || [];

//   const isUpcomingEvent = requestEventData?.status === 'Upcoming';

//   //ADD New
//   const filtedListForPresentEvent = showPrcingCategories.filter(
//     (item) => item.id !== '' && item.name !== ''
//   );

//   const filteredCategories = registrationCategoriesList?.filter((category) => {
//     const isInEventPricing = eventPricing.some(
//       (pricing) => pricing?.category === category?.name
//     );

//     const isActive = category?.status === '1';

//     const isInactiveAndMatchingUser =
//       category?.status === '0' && category?.user_id === userData?.id;

//     const isVisible = ['0', '1']?.includes(category?.is_display);

//     return isInEventPricing || isActive || isInactiveAndMatchingUser;
//   });

//   const [inrPrice, setINRPrice] = useState('');
//   const fetchUSDPrice = () => {
//     fetch(`https://api.exchangerate-api.com/v4/latest/USD`)
//       .then((response) => {
//         return response.json();
//       })
//       .then((data) => {
//         // setUSDPrice(data?.rates["USD"]);
//         setINRPrice(data?.rates['INR']);
//       });
//   };
//   useEffect(() => {
//     fetchUSDPrice();
//   }, []);

//   const { exchange_rate } = getDataFromLocalStorage();
//   const inrValue = inrPrice ? inrPrice : exchange_rate;
//   const handleCurrencyChange = (e) => {
//     const newCurrency = e.target.value;

//     setData((prevData) => {
//       const updatedEventPricing = prevData.event_pricing.map((event) => ({
//         ...event,
//         physical: event.physical.map((item) => {
//           if (initialCurrency === 'INR' && newCurrency === 'USD') {
//             return {
//               ...item,
//               oral: parseFloat(item.oral / inrValue),
//               poster: parseFloat(item.poster / inrValue),
//               onSpot: parseFloat(item.onSpot / inrValue),
//             };
//           } else if (initialCurrency === 'USD' && newCurrency === 'INR') {
//             return {
//               ...item,
//               oral: parseFloat(item.oral * inrValue),
//               poster: parseFloat(item.poster * inrValue),
//               onSpot: parseFloat(item.onSpot * inrValue),
//             };
//           } else {
//             return item;
//           }
//         }),
//         virtual: event.virtual.map((item) => {
//           if (initialCurrency === 'INR' && newCurrency === 'USD') {
//             return {
//               ...item,
//               oral: parseFloat(item.oral / inrValue),
//               poster: parseFloat(item.poster / inrValue),
//               onSpot: parseFloat(item.onSpot / inrValue),
//             };
//           } else if (initialCurrency === 'USD' && newCurrency === 'INR') {
//             return {
//               ...item,
//               oral: parseFloat(item.oral * inrValue),
//               poster: parseFloat(item.poster * inrValue),
//               onSpot: parseFloat(item.onSpot * inrValue),
//             };
//           } else {
//             return item;
//           }
//         }),
//       }));

//       if (newCurrency !== initialCurrency) {
//         setInitialCurrency(newCurrency);
//       }

//       return {
//         ...prevData,
//         currency_type: newCurrency,
//         event_pricing: updatedEventPricing,
//       };
//     });
//   };

//   return (
//     <>
//       {isLoading ? (
//         <div className="cpt-80 cpb-80">
//           <Loader size="md" />
//         </div>
//       ) : (
//         <div id="new-registration-event-form-block">
//           <div className="d-flex gap-2 align-items-center mb-3">
//             <div className="text-18-500 color-black-olive">
//               Pricing Categories
//             </div>
//           </div>
//           {pricingCategories?.map((elem, index) => {
//             const isDisableForUpcoming =
//               showPrcingCategories?.find(
//                 (o) =>
//                   o?.name === elem.category &&
//                   o?.status === '0' &&
//                   o?.is_display === '1'
//               )?.status === '0';

//             return (
//               <div className="row" key={index}>
//                 {elem?.category ? (
//                   <div className="col-xl-6 col-md-6 cmb-22">
//                     <CreatablePricingDropdown
//                       label={`Pricing Category ${index + 1}`}
//                       id={`pricing_categories-${index}`}
//                       placeholder="Select Price Category"
//                       options={showPrcingCategories}
//                       optionKey={'name'}
//                       optionValue={'name'}
//                       value={elem?.category}
//                       onChange={(e) => {
//                         handelChangeInput(e);
//                       }}
//                       isLoading={isCateLoading}
//                       selectedList={pricingCategories || []}
//                       disabled={
//                          ||
//                         (userData?.user_type === '6'
//                           ? isUpcomingEvent
//                             ? isDisableForUpcoming
//                             : ''
//                           : '')
//                       }
//                     />
//                   </div>
//                 ) : (
//                   <div className="col-xl-6 col-md-6 cmb-22">
//                     <CreatablePricingDropdown
//                       label={`Pricing Category ${index + 1}`}
//                       id={`pricing_categories-${index}`}
//                       placeholder="Select Price Category"
//                       options={filtedListForPresentEvent}
//                       value={elem?.category}
//                       onChange={(e) => {
//                         handelChangeInput(e);
//                       }}
//                       isLoading={isCateLoading}
//                       selectedList={pricingCategories || []}
//                       disabled={
//                         isViewMode ||
//                         (userData?.user_type === '6'
//                           ? isUpcomingEvent
//                             ? isDisableForUpcoming
//                             : ''
//                           : '')
//                       }
//                     />
//                   </div>
//                 )}
//                 <div className="col-xl-6 col-md-6 d-flex align-items-end cmb-22">
//                   {pricingCategories?.length > 1 &&
//                     deleteAccess === '1' &&
//                     !isViewMode && (
//                       <Button
//                         isSquare
//                         btnStyle="primary-gray"
//                         text="Delete"
//                         className="pt-2 pb-2 ps-3 pe-3"
//                         icon={
//                           <img
//                             src={icons.deleteIcon}
//                             alt="delete"
//                             className="h-21 me-3"
//                           />
//                         }
//                         onClick={() => {
//                           deleteCategory('pricing_categories', index);
//                         }}
//                       />
//                     )}
//                 </div>
//               </div>
//             );
//           })}

//           {!isViewMode && (updateAccess === '1' || createAccess === '1') && (
//             <div className="d-flex gap-3">
//               <Button
//                 text={isCategory ? 'Update' : 'Add'}
//                 btnStyle="primary-dark"
//                 className="text-15-500 ps-3 pe-3"
//                 disabled={
//                   isCategory
//                     ? isEqual(pricingCategories, category)
//                     : isCategoryBtn
//                 }
//                 onClick={updateCategory}
//                 btnLoading={categoryBtnLoading}
//               />
//               <Button
//                 text="+ Add Another Category"
//                 btnStyle="primary-light"
//                 className="text-15-500 ps-3 pe-3"
//                 onClick={() => {
//                   addCategory('pricing_categories');
//                 }}
//                 disabled={!isPriceBtn}
//               />
//             </div>
//           )}
//           <div>
//             <div className="col-xl-6 col-md-6 d-flex align-items-end cmb-22 cmt-10">
//               <Dropdown
//                 id="currency_type"
//                 placeholder="Select Currency"
//                 options={currencyType}
//                 value={data?.currency_type}
//                 optionKey="id"
//                 optionValue="label"
//                 label="Currency Type"
//                 onChange={handleCurrencyChange}
//                 disabled={isViewMode}
//               />
//             </div>
//           </div>
//           {isCategory && (
//             <>
//               <div className="text-18-500 color-black-olive mb-3 cpt-30">
//                 Event Pricing
//               </div>
//               <Table bordered responsive className="mb-0">
//                 <thead className="table-header-container">
//                   <tr className="table-header-row-container">
//                     <th className="header-cell">Registration Categories</th>
//                     <th className="header-cell" />
//                     {category?.map((elem, index) => {
//                       return (
//                         <th className="header-cell" key={index}>
//                           {elem?.category}
//                         </th>
//                       );
//                     })}
//                     {eventPricing?.length > 1 && <th className="header-cell" />}
//                   </tr>
//                 </thead>
//                 <tbody className="table-body-container">
//                   {eventPricing?.map((elem, index) => {
//                     const { physical, virtual } = elem;
//                     const isDisableForUpcomingEventPricing =
//                       showEventPricingCategories?.find(
//                         (o) =>
//                           o?.name === elem.category &&
//                           o?.status === '0' &&
//                           o?.is_display === '1'
//                       )?.status === '0';

//                     return (
//                       <React.Fragment key={index}>
//                         <tr className="table-body-row-container">
//                           <td
//                             className="text-14-400 color-black-olive row-cell ps-2 pe-2"
//                             rowSpan={2}
//                           >
//                             <CreatablePricingDropdown
//                               id={`event_pricing-${index}`}
//                               placeholder="Select Category"
//                               options={filteredCategories}
//                               optionKey={'name'}
//                               optionValue={'name'}
//                               value={elem?.category}
//                               onChange={(e) => {
//                                 handelChangeInput(e);
//                               }}
//                               isLoading={isCateLoading}
//                               selectedList={eventPricing || []}
//                               disabled={
//                                 isViewMode ||
//                                 (userData?.user_type === '6'
//                                   ? isUpcomingEvent
//                                     ? isDisableForUpcomingEventPricing
//                                     : ''
//                                   : '')
//                               }
//                             />
//                           </td>
//                           <td className="text-16-500 color-black-olive row-cell ps-5 pe-5">
//                             Physical
//                           </td>
//                           {physical.map((pElem, pIndex) => {
//                             return (
//                               <td className="row-cell ps-2 pe-2" key={pIndex}>
//                                 <div className="d-flex gap-2">
//                                   <TextInput
//                                     id={`event_pricing-${index}-physical-${pIndex}-oral`}
//                                     placeholder="Early Bird Price"
//                                     onChange={handelChangeInput}
//                                     value={pElem.oral}
//                                     disabled={isViewMode}
//                                   />
//                                   <TextInput
//                                     id={`event_pricing-${index}-physical-${pIndex}-poster`}
//                                     placeholder="Final Price"
//                                     onChange={handelChangeInput}
//                                     value={pElem.poster}
//                                     disabled={isViewMode}
//                                   />
//                                   <TextInput
//                                     id={`event_pricing-${index}-physical-${pIndex}-onSpot`}
//                                     placeholder="On Spot Price"
//                                     onChange={handelChangeInput}
//                                     value={pElem.onSpot}
//                                     disabled={isViewMode}
//                                   />
//                                 </div>
//                               </td>
//                             );
//                           })}
//                           {eventPricing.length > 1 &&
//                             deleteAccess === '1' &&
//                             !isViewMode && (
//                               <td
//                                 className="text-14-400 color-black-olive row-cell ps-2 pe-2"
//                                 rowSpan={2}
//                               >
//                                 <Button
//                                   isSquare
//                                   btnStyle="light-outline"
//                                   className="pt-1 pb-1 ps-2 pe-2 h-35"
//                                   icon={
//                                     deleteLoading ===
//                                     `event_pricing${index}` ? (
//                                       ''
//                                     ) : (
//                                       <img
//                                         src={icons.deleteIcon}
//                                         alt="delete"
//                                         className="h-21"
//                                       />
//                                     )
//                                   }
//                                   onClick={() => {
//                                     deleteCategory(
//                                       'event_pricing',
//                                       index,
//                                       elem.id
//                                     );
//                                   }}
//                                   btnLoading={
//                                     deleteLoading === `event_pricing_${index}`
//                                   }
//                                 />
//                               </td>
//                             )}
//                         </tr>
//                         <tr className="table-body-row-container">
//                           <td className="text-16-500 color-black-olive row-cell ps-5 pe-5">
//                             Virtual
//                           </td>
//                           {virtual.map((vElem, vIndex) => {
//                             return (
//                               <td className="row-cell ps-2 pe-2" key={vIndex}>
//                                 <div className="d-flex gap-2">
//                                   <TextInput
//                                     id={`event_pricing-${index}-virtual-${vIndex}-oral`}
//                                     placeholder="Early Bird Price"
//                                     onChange={handelChangeInput}
//                                     value={vElem.oral}
//                                     disabled={isViewMode}
//                                   />
//                                   <TextInput
//                                     id={`event_pricing-${index}-virtual-${vIndex}-poster`}
//                                     placeholder="Final Price"
//                                     onChange={handelChangeInput}
//                                     value={vElem.poster}
//                                     disabled={isViewMode}
//                                   />
//                                   <TextInput
//                                     id={`event_pricing-${index}-virtual-${vIndex}-onSpot`}
//                                     placeholder="On Spot Price"
//                                     onChange={handelChangeInput}
//                                     value={vElem.onSpot}
//                                     disabled={isViewMode}
//                                   />
//                                 </div>
//                               </td>
//                             );
//                           })}
//                         </tr>
//                       </React.Fragment>
//                     );
//                   })}
//                 </tbody>
//               </Table>

//               {!isViewMode &&
//                 (updateAccess === '1' || createAccess === '1') && (
//                   <div className="d-flex mt-2">
//                     <Button
//                       text="+ Add Another Category"
//                       btnStyle="primary-light"
//                       className="text-15-500 ps-3 pe-3"
//                       // disabled={!isEventBtn}
//                       onClick={() => {
//                         addCategory('event_pricing');
//                       }}
//                     />
//                   </div>
//                 )}

//               <div className="text-18-500 color-black-olive mb-3 cpt-30">
//                 Add On Pricing
//               </div>
//               <Table bordered>
//                 <thead className="table-header-container">
//                   <tr className="table-header-row-container">
//                     <th className="header-cell">Add On Categories</th>
//                     <th className="header-cell">Premium Members</th>
//                     <th className="header-cell">Non-Premium Members</th>
//                     {addOnPricing.length > 1 && <th className="header-cell" />}
//                   </tr>
//                 </thead>
//                 <tbody className="table-body-container">
//                   {addOnPricing.map((elem, index) => {
//                     const { category, premium_members, non_premium_members } =
//                       elem;
//                     return (
//                       <tr className="table-body-row-container" key={index}>
//                         <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
//                           <TextInput
//                             id={`add_on_pricing-${index}-category`}
//                             onChange={handelChangeInput}
//                             placeholder="Enter Add ons Category"
//                             value={category}
//                             disabled={isViewMode}
//                           />
//                         </td>
//                         <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
//                           <TextInput
//                             id={`add_on_pricing-${index}-premium_members`}
//                             onChange={handelChangeInput}
//                             placeholder="Enter Price"
//                             value={premium_members}
//                             disabled={isViewMode}
//                           />
//                         </td>
//                         <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
//                           <TextInput
//                             id={`add_on_pricing-${index}-non_premium_members`}
//                             onChange={handelChangeInput}
//                             placeholder="Enter Price"
//                             value={non_premium_members}
//                             disabled={isViewMode}
//                           />
//                         </td>

//                         {addOnPricing.length > 1 &&
//                           deleteAccess === '1' &&
//                           !isViewMode && (
//                             <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
//                               <Button
//                                 isSquare
//                                 btnStyle="light-outline"
//                                 className="pt-1 pb-1 ps-2 pe-2 h-35"
//                                 icon={
//                                   deleteLoading ===
//                                   `add_on_pricing_${index}` ? (
//                                     ''
//                                   ) : (
//                                     <img
//                                       src={icons.deleteIcon}
//                                       alt="delete"
//                                       className="h-21"
//                                     />
//                                   )
//                                 }
//                                 onClick={() => {
//                                   deleteCategory(
//                                     'add_on_pricing',
//                                     index,
//                                     elem.id
//                                   );
//                                 }}
//                                 btnLoading={
//                                   deleteLoading === `add_on_pricing_${index}`
//                                 }
//                               />
//                             </td>
//                           )}
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </Table>
//               {!isViewMode &&
//                 (updateAccess === '1' || createAccess === '1') && (
//                   <div className="d-flex">
//                     <Button
//                       text="+ Add Another Category"
//                       btnStyle="primary-light"
//                       className="text-15-500 ps-3 pe-3"
//                       disabled={!isAddOnBtn}
//                       onClick={() => {
//                         addCategory('add_on_pricing');
//                       }}
//                     />
//                   </div>
//                 )}

//               <div className="text-18-500 color-black-olive mb-3 cpt-30">
//                 Dashboard Pricing Selection
//               </div>
//               <Table bordered>
//                 <thead className="table-header-container">
//                   <tr className="table-header-row-container">
//                     <th className="header-cell">Registration Categories</th>

//                     {category.map((elem, index) => {
//                       return (
//                         <th className="header-cell" key={index}>
//                           {elem.category}
//                         </th>
//                       );
//                     })}
//                   </tr>
//                 </thead>
//                 <tbody className="table-body-container">
//                   <tr className="table-body-row-container">
//                     <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
//                       ALL
//                     </td>
//                     {category.map((elem, index) => {
//                       let isSelectALL = true;
//                       dashboardPricingSelection?.forEach((p) => {
//                         p?.data?.forEach((c) => {
//                           if (elem?.id === c?.id) {
//                             if (!c?.is_checked) {
//                               isSelectALL = false;
//                             }
//                           }
//                         });
//                       });
//                       return (
//                         <td
//                           className="text-14-400 color-black-olive row-cell"
//                           key={index}
//                         >
//                           <div className="center-flex">
//                             <CheckBox
//                               className="checkbox-size-24"
//                               type="ACTIVE"
//                               onClick={() => {
//                                 let oldData = cloneDeep(data);
//                                 oldData?.dashboard_pricing_selection.map(
//                                   (p) => {
//                                     let newP = p;
//                                     newP.data = p.data.map((c) => {
//                                       let newC = c;
//                                       if (c.id === elem.id) {
//                                         newC.is_checked = !isSelectALL;
//                                       }
//                                       return newC;
//                                     });
//                                     return newP;
//                                   }
//                                 );

//                                 setData(oldData);
//                               }}
//                               isChecked={isSelectALL}
//                             />
//                           </div>
//                         </td>
//                       );
//                     })}
//                   </tr>
//                   {dashboardPricingSelection?.map((elem, index) => {
//                     return (
//                       <tr className="table-body-row-container" key={index}>
//                         <td className="text-14-400 color-black-olive row-cell ps-2 pe-2">
//                           {elem.name}
//                         </td>
//                         {elem?.data.map((cElem, cIndex) => {
//                           return (
//                             <td
//                               className="text-14-400 color-black-olive row-cell"
//                               key={cIndex}
//                             >
//                               <div className="center-flex">
//                                 <CheckBox
//                                   className="checkbox-size-24"
//                                   type="PRIMARY-ACTIVE"
//                                   onClick={() => {
//                                     let oldData = cloneDeep(data);

//                                     oldData.dashboard_pricing_selection[
//                                       index
//                                     ].data[cIndex].is_checked =
//                                       !cElem.is_checked;
//                                     setData(oldData);
//                                   }}
//                                   isChecked={cElem.is_checked}
//                                 />
//                               </div>
//                             </td>
//                           );
//                         })}
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </Table>

//               <>
//                 {!isViewMode && (
//                   <div className="d-flex justify-content-center gap-4 cmt-40 cmb-20">
//                     {(updateAccess === '1' || createAccess === '1') && (
//                       <Button
//                         text="Save"
//                         btnStyle="primary-dark"
//                         className="cps-40 cpe-40 h-37"
//                         btnLoading={isBtnLoading}
//                         disabled={isbtnEnable}
//                         onClick={() => {
//                           if (!isEventBtn) {
//                             dispatch(
//                               throwError({
//                                 message:
//                                   'Please enter a amount in the event pricing table.',
//                               })
//                             );
//                             return;
//                           }
//                           updatePrice(data);
//                         }}
//                       />
//                     )}
//                     <Button
//                       text="Reset"
//                       btnStyle="light-outline"
//                       className="cps-40 cpe-40 h-37"
//                       onClick={() => {
//                         setData(oldData);
//                       }}
//                     />
//                     <Button
//                       text={'Procced Next'}
//                       btnStyle="light-outline"
//                       className={'cps-20 cpe-20 h-37'}
//                       onClick={() => {
//                         handleNext('registration');
//                       }}
//                     />
//                   </div>
//                 )}
//               </>
//             </>
//           )}
//         </div>
//       )}
//     </>
//   );
// };

// export default NewRegistrationEventRequestForm;
